import React from 'react';
import { Box, Flex, Text, Heading, Link } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import {
  UserSearch,
  MessagesSquare,
  Joystick,
  Slack,
  Flame,
  ClipboardCheck,
  Send,
  ArrowRight,
  Sparkles,
  Users,
  Check,
  Earth,
} from 'lucide-react';
import usePageTitle from '../../hooks/usePageTitle';
import { trackEvent } from '../../helpers/analytics';
import MainLayout from '../../components/layout/Main';
import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';

const AiAgents = () => {
  usePageTitle(`AI Agents`);

  const ctaLink = 'https://scheduler.zoom.us/maveric/30-mins-y6mfsfgf';

  const onEarlyAccessClick = () => {
    trackEvent('ai_agents:early_access_click');
  };

  const features = [
    {
      icon: UserSearch,
      title: 'Find smarter, faster',
      description: 'Identify the right person based on career milestones, network connections, and verified context..',
    },
    {
      icon: Earth,
      title: '+1B profiles, 100k+ sources',
      description:
        'Tap into our global pool enriched from GitHub, Crunchbase, Stack Overflow, and your internal tools.',
    },
    {
      icon: Flame,
      title: 'Start with warm leads',
      description: 'Our agents prioritize high-intent, network-connected, and mission-aligned candidates first.',
    },
    {
      icon: MessagesSquare,
      title: 'Natural language interface',
      description:
        'Just tell the agent what you need in plain English. No filters or checkboxes (unless you want them).',
    },
    {
      icon: Joystick,
      title: "You're always in control",
      description: 'Agents automate workflows, not decisions. You decide who gets contacted, how, and when.',
    },
    {
      icon: Slack,
      title: 'Works where you do',
      description:
        'Launch agents directly from Getro, Slack or Email. No need to change your workflow, just upgrade it.',
    },
  ];

  const agents = [
    {
      icon: Users,
      iconBackground: '#FFE5FF',
      title: 'Application Boost Agent',
      subtitle: 'Boost qualified applicants for any job.',
      description: 'Automatically get highly qualified candidates for the jobs you care the most, with no manual work.',
      benefits: [
        'Attract highly qualified applicants',
        'Help portfolio companies fill key roles',
        'Save hours of sourcing and promotion',
      ],
    },
    {
      icon: ClipboardCheck,
      iconBackground: '#FFE6E0',
      title: 'Calibration Agent',
      subtitle: 'Refine every Application Boost for accuracy',
      description:
        'Before launching a boost campaign, the agent learns what “great” looks like by reviewing a few profiles with you.',
      benefits: ['Optimize boost campaigns', 'Improve targeting and fit', 'You or the hiring manager can do it'],
    },
    {
      icon: Send,
      iconBackground: '#F4F0FF',
      title: 'Job Board Agent',
      subtitle: 'Turn job board visitors into strong applicants.',
      description: 'Guides visitors to the right roles, improving conversion and reducing noise for hiring teams.',
      benefits: [
        'Reduce irrelevant applications',
        'Enhance the candidate experience',
        'Boost conversion from visitor to applicant',
      ],
    },
  ];

  return (
    <MainLayout>
      <MainLayoutHeader title="AI Agents" />
      {/* Hero Section */}
      <Flex
        px={4}
        py={5}
        sx={{
          maxWidth: '720px',
          mx: 'auto',
          textAlign: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Glow 1 */}
        <Box
          sx={{
            position: 'absolute',
            zIndex: -1,
            left: -200,
            bottom: 0,
            width: 240,
            height: 180,
            borderRadius: 999,
            opacity: 0.3,
            background: '#FA816B',
            filter: 'blur(150px)',
            transform: 'rotate(-45deg)',
            flexShrink: 0,
          }}
        />
        <Flex
          sx={{
            fontSize: '12px',
            fontWeight: 'semibold',
            textTransform: 'uppercase',
            fontStyle: 'italic',
            color: 'text.subtle',
            px: 2,
            py: 1,
            borderRadius: '99px',
            mb: 3,
            border: '1px solid',
            borderColor: 'border.subtle',
          }}
        >
          <Box as={Sparkles} width="16px" height="16px" mr={2} color="#FF9257" />
          <Text as="span">Beta feature</Text>
        </Flex>

        <Heading
          as="h1"
          sx={{
            fontSize: '40px',
            fontWeight: '700',
            color: 'text.dark',
            mb: 3,
            lineHeight: '1.2',
            background: 'linear-gradient(90deg, #4900FF -3.61%, #D400FF 46.47%, #FF9257 104.68%);',
            backgroundClip: 'text;',
            '-webkit-background-clip': 'text;',
            '-webkit-text-fill-color': 'transparent;',
          }}
        >
          Your unfair advantage in hiring
        </Heading>

        <Text
          sx={{
            fontSize: [2, 3],
            fontWeight: '500',
            color: 'text.dark',
            mb: 4,
            maxWidth: '600px',
            mx: 'auto',
            lineHeight: '1.5',
          }}
        >
          Meet our new AI Agents built to amplify your expertise
          <br /> and scale your support to your companies.
        </Text>

        <Button
          as={Link}
          href={ctaLink}
          target="_blank"
          size="large"
          onClick={onEarlyAccessClick}
          icon={ArrowRight}
          iconPosition="right"
          sx={{
            p: '24px 40px',
            background: 'linear-gradient(70deg, #4900FF -40%, #D400FF 60%, #FF9257 90%, #4900FF 100%), #4900FF;',
            backgroundSize: '300% 100%',
            transition: 'all 0.4s ease-in-out',
            svg: {
              transition: 'all 0.4s ease-in-out',
            },
            '&:hover': {
              backgroundPosition: '-80% 0',
              svg: {
                transform: 'translateX(8px)',
              },
            },
          }}
        >
          Get early access
        </Button>
      </Flex>

      {/* Why Getro AI Agents Section */}
      <Box px={4} py={6} sx={{ position: 'relative' }}>
        {/* Glow 2 */}
        <Box
          sx={{
            position: 'absolute',
            zIndex: -1,
            right: 0,
            bottom: 0,
            width: 240,
            height: 180,
            borderRadius: 999,
            opacity: 0.15,
            bg: 'purple.400',
            filter: 'blur(150px)',
            transform: 'rotate(-45deg)',
            flexShrink: 0,
          }}
        />
        <Box textAlign="center" mb={4}>
          <Heading as="h2" fontSize={6} fontWeight="bold" color="text.dark" mb={5}>
            Why Getro AI Agents?
          </Heading>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: ['1fr', 'repeat(2, 1fr)', 'repeat(3, 1fr)'],
            gap: 0,
            maxWidth: '920px',
            mx: 'auto',
          }}
        >
          {features.map((feature) => (
            <Box
              key={feature.title}
              sx={{
                '&:nth-child(1)': {
                  borderBottom: '1px solid',
                  borderRight: ['none', '1px solid', '1px solid'],
                  borderColor: ['border.subtle', 'border.subtle', 'border.subtle'],
                  pt: 0,
                  pr: [0, 4, 4],
                  pb: [4, 4, 4],
                  pl: 0,
                },
                '&:nth-child(2)': {
                  borderBottom: '1px solid',
                  borderRight: ['none', 'none', '1px solid'],
                  borderColor: ['border.subtle', 'border.subtle', 'border.subtle'],
                  pt: [4, 0, 0],
                  pr: [0, 0, 4],
                  pb: [4, 4, 4],
                  pl: [0, 4, 4],
                },
                '&:nth-child(3)': {
                  borderBottom: '1px solid',
                  borderRight: ['none', '1px solid', 'none'],
                  borderColor: ['border.subtle', 'border.subtle', 'border.subtle'],
                  pt: [4, 4, 0],
                  pr: [0, 4, 0],
                  pb: [4, 4, 4],
                  pl: [0, 0, 4],
                },
                '&:nth-child(4)': {
                  borderBottom: ['1px solid', '1px solid', 'none'],
                  borderRight: ['none', 'none', '1px solid'],
                  borderColor: ['border.subtle', 'border.subtle', 'border.subtle'],
                  pt: [4, 4, 4],
                  pr: [0, 0, 0],
                  pb: [4, 4, 0],
                  pl: [0, 4, 0],
                },
                '&:nth-child(5)': {
                  borderBottom: ['1px solid', 'none', 'none'],
                  borderRight: ['none', '1px solid', '1px solid'],
                  borderColor: ['border.subtle', 'border.subtle', 'border.subtle'],
                  pt: [4, 4, 4],
                  pr: [0, 4, 4],
                  pb: [4, 0, 0],
                  pl: [0, 0, 4],
                },
                '&:nth-child(6)': {
                  pt: [4, 4, 4],
                  pr: [0, 4, 0],
                  pb: [0, 0, 0],
                  pl: [0, 4, 4],
                },
              }}
            >
              <Box
                as={feature.icon}
                width="24px"
                height="24px"
                mb={3}
                color="#D400FF"
                sx={{
                  fill: 'none',
                  strokeWidth: '2',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                }}
              />
              <Heading as="h3" fontSize={2} fontWeight="semibold" color="text.dark" mb={2}>
                {feature.title}
              </Heading>
              <Text fontSize={1} color="text.subtle" lineHeight="1.5">
                {feature.description}
              </Text>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Meet your agents Section */}
      <Box px={4} py={6} sx={{ position: 'relative' }}>
        {/* Glow 3 */}
        <Box
          sx={{
            position: 'absolute',
            zIndex: -1,
            left: 0,
            bottom: -100,
            width: 240,
            height: 180,
            borderRadius: 999,
            opacity: 0.25,
            bg: '#E77DFC',
            filter: 'blur(150px)',
            transform: 'rotate(-45deg)',
            flexShrink: 0,
          }}
        />
        <Box textAlign="center" mb={5}>
          <Heading as="h2" fontSize={6} fontWeight="bold" color="text.dark" mb={3}>
            Meet your agents
          </Heading>
          <Text fontSize={3} maxWidth="920px" mx="auto">
            Your network engineers to help you surface the right people, connect faster, and unlock warm paths.
          </Text>
        </Box>

        <Flex
          flexDirection="column"
          sx={{
            gap: 3,
            maxWidth: '920px',
            mx: 'auto',
          }}
        >
          {agents.map((agent) => (
            <Flex
              key={agent.title}
              flexDirection={['column', 'column', 'row']}
              alignItems="flex-start"
              sx={{
                gap: 3,
                p: '24px',
                bg: 'white',
                borderRadius: '8px',
                boxShadow: '0 1px 4px 0 rgba(58, 7, 187, 0.06), 0 2px 32px 0 rgba(58, 7, 187, 0.06)',
              }}
            >
              <Flex alignItems="center" sx={{ borderRadius: '8px', bg: agent.iconBackground, p: 2, flexShrink: 0 }}>
                <Box as={agent.icon} width="24px" height="24px" />
              </Flex>

              <Flex flexDirection="column" gap={2}>
                <Heading as="h3" fontSize={4} fontWeight="bold" color="text.dark" mb={2}>
                  {agent.title}
                </Heading>

                <Flex sx={{ gap: 4 }} flexDirection={['column', 'column', 'row']}>
                  <Flex flexDirection="column" sx={{ gap: 3 }}>
                    <Text fontSize={2} fontWeight="500">
                      {agent.subtitle}
                    </Text>

                    <Text fontSize={2} lineHeight="1.6">
                      {agent.description}
                    </Text>
                  </Flex>

                  <Flex width={['100%', '100%', '320px']} flexShrink={0} flexDirection="column" sx={{ gap: 2 }}>
                    {agent.benefits.map((benefit) => (
                      <Flex key={benefit} sx={{ gap: 2 }}>
                        <Box as={Check} width="16px" height="16px" color="#FF9F8A" flexShrink={0} mt="4px" />
                        <Text fontSize={2}>{benefit}</Text>
                      </Flex>
                    ))}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Box>

      {/* Bottom CTA Section */}
      <Box px={3} py={5} mb={5} textAlign="center">
        <Heading as="h2" fontSize={6} fontWeight="bold" color="text.dark" mb={4}>
          Ready to scale your impact?
        </Heading>

        <Button
          as={Link}
          href={ctaLink}
          target="_blank"
          size="large"
          onClick={onEarlyAccessClick}
          icon={ArrowRight}
          iconPosition="right"
          sx={{
            p: '24px 40px',
            background: 'linear-gradient(70deg, #4900FF -40%, #D400FF 60%, #FF9257 90%, #4900FF 100%), #4900FF;',
            backgroundSize: '300% 100%',
            transition: 'all 0.4s ease-in-out',
            svg: {
              transition: 'all 0.4s ease-in-out',
            },
            '&:hover': {
              backgroundPosition: '-80% 0',
              svg: {
                transform: 'translateX(8px)',
              },
            },
          }}
        >
          Get early access
        </Button>

        <Text fontSize={1} fontStyle="italic" mt={3} color="text.subtle">
          Limited Beta spots
        </Text>
      </Box>
    </MainLayout>
  );
};

export default AiAgents;
